import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { SINGLE_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getEntityRef, getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { Alert } from "@client/controls/Alert/component";
import { remove } from "@client/lib/array";
import { deletePaymentFailedOrderId, getPaymentFailedOrderId } from "@client/lib/cookie";
import { useDidUpdate } from "@client/lib/hooks";
import { getEntitySelector } from "@client/lib/layout";
import { addPrefix } from "@client/lib/object";
import { type GenericMap } from "@client/types";
import { type PargoPickupPoint } from "@common/components/Integrations/PargoPickup";
import { type OptionItem, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import {
  getIsSameAddress,
  mapFromBillingAddress,
  mapFromShippingAddress,
  mapToBillingAddress
} from "@common/lib/address";
import classNames from "classnames";
import { identity } from "lodash";
import * as React from "react";
import Sticky from "react-stickynode";
import { mapAddress } from "../lib";
import { type Checkout } from "../types";
import AdditionalInfo from "./AdditionalInfo/container";
import Address from "./Address/container";
import Consent from "./Consent/container";
import Contact from "./Contact/container";
import Event from "./Event/container";
import InventoryFailureModal from "./InventoryFailureModal/container";
import NetworkInformation from "./NetworkInformation/container";
import OrderSummary from "./OrderSummary/container";
import Payment from "./Payment/container";
import SettingsTracker from "./SettingsTracker/container";
import ShippingMethod from "./ShippingMethod/component";
import SpecialOffer from "./SpecialOffer/container";
import Variants from "./Variants/container";
import { Context, type ContextProps, type Listener } from "./context";
import { getIsPargoPickup, getOrderAfterShippingRuleChange } from "./lib";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  billingAddress: Checkout.Api.UserAddress | null;
  canPlaceOrder: boolean;
  cart: Checkout.Cart;
  consentLocation?: "above" | "below";
  error: string | null;
  event: Checkout.View.Event | null;
  failureReason: Checkout.PlaceOrderFailureReason | null;
  fieldStyle: React.CSSProperties;
  isSideBar: boolean;
  noImageUrl: string;
  orderId: string | null;
  orderInfoTriggerParams: Checkout.Api.GetOrderInfoTriggerParams;
  requiresShipping: boolean;
  shippingAddress: Checkout.Api.UserAddress | null;
  shippingRuleId: string | null;
  shippingRules: Checkout.ShippingRule[];
  shouldAdjust: boolean;
  shouldPay: boolean;
  status: Checkout.Status;
};

export interface ActionProps {
  onAdjustOrder: () => void;
  onPlaceOrder: (data: Checkout.Order) => void;
  onPrepareOrderInfo: (data: Checkout.AdjustOrderInfo, coupons: string[] | null) => void;
  setOrder: (order: Checkout.Order) => void;
}
export type Props = ActionProps & MapProps;

export default (props: Props) => {
  const i18n = useI18n();

  const isCancelled = React.useRef(false);
  const [isPlacing, setIsPlacing] = React.useState<boolean>(false);
  const [shippingRegionOptions, setShippingRegionOptions] = React.useState<OptionItem<string>[]>([]);
  const [order, setOrder] = React.useState<Checkout.Order>({
    ...mapAddress(props.billingAddress, "billing"),
    ...mapAddress(props.shippingAddress, "shipping"),
    ...props.event,
    includeOffer: false,
    isSameAsShipping: props.cart.requiresShipping
      ? getIsSameAddress(props.billingAddress ?? {}, props.shippingAddress ?? {})
      : null,
    shippingRuleId: props.shippingRuleId,
    shippingPickupPointCode: null
  });

  useDidUpdate(
    () =>
      setOrder((x) => ({
        ...x,
        isSameAsShipping: props.cart.requiresShipping
          ? getIsSameAddress(mapFromShippingAddress(x), mapFromBillingAddress(x))
          : null
      })),
    [props.cart.requiresShipping]
  );

  const isPaymentFailed = React.useMemo(() => {
    const failedOrderId = getPaymentFailedOrderId();
    if (failedOrderId) {
      deletePaymentFailedOrderId();
      return failedOrderId.toLocaleLowerCase() === props.orderId?.toLocaleLowerCase();
    }
    return false;
  }, []);

  const isPargoPickup = React.useMemo(
    () => getIsPargoPickup(props.shippingRules, order.shippingRuleId),
    [props.shippingRuleId, order.shippingRuleId]
  );

  // TODO: this overwrites the address when you change it in the UI. Probably this code isn't needed
  // React.useEffect(
  //   () =>
  //     setOrder({
  //       ...order,
  //       ...mapAddress(props.billingAddress, "billing"),
  //       ...mapAddress(props.shippingAddress, "shipping"),
  //       shippingRuleId: props.shippingRuleId,
  //     }),
  //   [props.billingAddress, props.shippingAddress, props.shippingRuleId, setOrder]
  // );

  React.useEffect(
    () => () => {
      isCancelled.current = true;
    },
    []
  );

  React.useEffect(() => {
    props.setOrder(order as Checkout.Order);
  }, [props.setOrder, order]);

  const contextValue = React.useMemo((): ContextProps => {
    const listeners: Listener[] = [];

    return {
      listeners,
      subscribeOnSubmit: (listener: Listener) => {
        listeners.push(listener);
      },
      unsubscribeOnSubmit: (listener: Listener) => {
        const index = listeners.indexOf(listener);
        if (index >= 0) {
          listeners.splice(index, 1);
        }
      }
    };
  }, []);

  const adjustOrderInfo = React.useMemo(
    (): Checkout.AdjustOrderInfo => ({
      billingEmail: order.billingEmail ?? null,
      billingCountryCode: order.billingCountryCode ?? null,
      billingSubdivisionCode: order.billingSubdivisionCode ?? null,
      eventId: order.eventId ?? null,
      eventDateLocationId: order.eventDateLocationId ?? null,
      includeOffer: order.includeOffer,
      currencyCode: props.orderInfoTriggerParams.currencyCode || null,
      offerProductVariantId: props.orderInfoTriggerParams.offerProductVariantId ?? null,
      pargoPickupPoint: order.pargoPickupPoint,
      productId: props.orderInfoTriggerParams.productId ?? null,
      productVariantId: props.orderInfoTriggerParams.productVariantId ?? null,
      shippingCity: order.shippingCity,
      shippingCountryCode: order.shippingCountryCode,
      shippingSubdivisionCode: order.shippingSubdivisionCode,
      shippingRuleId: order.shippingRuleId ?? null
    }),
    [
      order.billingCity,
      order.billingEmail,
      order.billingCountryCode,
      order.billingSubdivisionCode,
      order.eventId,
      order.eventDateLocationId,
      order.includeOffer,
      order.shippingCity,
      order.shippingCountryCode,
      order.shippingSubdivisionCode,
      order.pargoPickupPoint,
      order.shippingRuleId,
      props.orderInfoTriggerParams.currencyCode,
      props.orderInfoTriggerParams.productId,
      props.orderInfoTriggerParams.productVariantId,
      props.orderInfoTriggerParams.offerProductVariantId
    ]
  );

  useDidUpdate(() => {
    props.onPrepareOrderInfo(adjustOrderInfo, null);

    const timeout = setTimeout(() => {
      if (isCancelled.current) return;

      props.onAdjustOrder();
    }, 100);

    return () => clearTimeout(timeout);
  }, [props.onAdjustOrder, props.onPrepareOrderInfo, adjustOrderInfo]);

  useDidUpdate(() => {
    props.onPrepareOrderInfo(adjustOrderInfo, null);
    props.onAdjustOrder();
  }, [props.onPrepareOrderInfo, props.onAdjustOrder, props.orderInfoTriggerParams]);

  useDidUpdate(() => {
    if (!props.shippingRules.some((x) => x.id === order.shippingRuleId)) {
      setOrder((x) => ({ ...x, shippingRuleId: null }));
    }
  }, [props.shippingRules, order.shippingRuleId]);

  const onApplyCoupon = React.useCallback(
    (coupon: string) => {
      if (!props.cart.coupons.includes(coupon)) {
        props.onPrepareOrderInfo(adjustOrderInfo, [...props.cart.coupons, coupon]);
      }
    },
    [adjustOrderInfo, props.cart.coupons, props.onPrepareOrderInfo]
  );

  const onBillingChange = React.useCallback((name: keyof Checkout.View.Address | "isSameAsShipping", value: any) => {
    if (name === "isSameAsShipping") {
      setOrder((x) =>
        value
          ? { ...x, isSameAsShipping: value, ...mapToBillingAddress(mapFromShippingAddress(x)) }
          : { ...x, isSameAsShipping: value }
      );
    } else {
      // NOTE: two consequent onChange would overwrite each other, if using order directly
      setOrder((x) => ({ ...x, [addPrefix(name, "billing")]: value }));
    }
  }, []);

  const onEventChange = React.useCallback((name: keyof Checkout.View.Event, value: any) => {
    setOrder((x) => ({ ...x, [name]: value }));
  }, []);

  const onRemoveCoupon = React.useCallback(
    (coupon: string) => {
      if (props.cart.coupons.includes(coupon)) {
        props.onPrepareOrderInfo(adjustOrderInfo, remove(props.cart.coupons, coupon));
      }
    },
    [adjustOrderInfo, props.cart.coupons, props.onAdjustOrder]
  );

  const onConsentChange = React.useCallback((consent: GenericMap<any>) => {
    // NOTE: two consequent onChange would overwrite each other, if using order directly
    setOrder((x) => ({ ...x, consent }));
  }, []);

  const onIncludeOfferChange = React.useCallback((includeOffer: boolean) => {
    // NOTE: two consequent onChange would overwrite each other, if using order directly
    setOrder((x) => ({ ...x, includeOffer }));
  }, []);

  const onShippingChange = React.useCallback((name: keyof Checkout.View.Address, value: any) => {
    // NOTE: two consequent onChange would overwrite each other, if using order directly
    setOrder((x) => ({
      ...x,
      [addPrefix(name, "shipping")]: value,
      [addPrefix(name, "billing")]: x.isSameAsShipping ? value : x[addPrefix(name, "billing")]
    }));
  }, []);

  const onEmailChange = React.useCallback((name: keyof Checkout.View.Address, value: any) => {
    // NOTE: two consequent onChange would overwrite each other, if using order directly
    setOrder((x) => ({ ...x, billingEmail: value }));
  }, []);

  const onShippingMethodChange = React.useCallback(
    (value: string) => {
      setOrder((x) => getOrderAfterShippingRuleChange(x, props.shippingRules, value));
    },
    [props.shippingRules, order.shippingRuleId]
  );

  const onShippingPickupPointChange = React.useCallback((data: PargoPickupPoint) => {
    setOrder((x) => ({
      ...x,
      pargoPickupPoint: data
    }));
  }, []);

  const onPlaceOrder = React.useCallback(async () => {
    setIsPlacing(true);
    try {
      const validateResults = await Promise.all(contextValue.listeners.map((x) => x.validate()));
      if (!validateResults.every((x) => !x)) return;

      const result: GenericMap<any>[] = [];
      for (const listener of contextValue.listeners) {
        const lr = await listener.getValue();
        result.push(lr);
      }

      const data = result.reduce((res: object, x: object) => ({ ...res, ...x }), {});
      return props.onPlaceOrder({
        ...(data as Checkout.Order),
        shippingEmail: props.requiresShipping ? data.email : null,
        billingEmail: data.email,
        shippingPickupPointCode: isPargoPickup ? order.pargoPickupPoint!.pargoPointCode : null
      });
    } finally {
      setIsPlacing(false);
    }
  }, [contextValue.listeners, props.onPlaceOrder, isPargoPickup, order, setIsPlacing, props.requiresShipping]);

  const isEstimatedTax = React.useMemo(
    () => !!shippingRegionOptions.length && !order.shippingSubdivisionCode && !order.shippingPickupPointCode,
    [shippingRegionOptions, order.shippingSubdivisionCode, order.shippingPickupPointCode]
  );

  return (
    <Context.Provider value={contextValue}>
      <SettingsTracker {...getRootContainerProps(props)} />
      {isPaymentFailed && <Alert color={SystemColor.Red}>{i18n.errorPaymentFailed}</Alert>}

      <FormStyleProvider {...getEntityRef(props)}>
        <div className={classNames("checkout_content", { with_side_bar: props.isSideBar })}>
          <div className="body">
            <NetworkInformation {...getRootContainerProps(props)} />
            <Contact
              {...getRootContainerProps(props)}
              email={props.shippingAddress?.email ?? props.billingAddress?.email ?? null}
              onChange={onEmailChange}
            />
            {props.requiresShipping && (
              <ShippingMethod
                apiInfo={props.apiInfo}
                onChange={onShippingMethodChange}
                onPickupPointChange={onShippingPickupPointChange}
                order={order}
                shippingRules={props.shippingRules}
              />
            )}
            {!isPargoPickup && (
              <Address
                {...getRootContainerProps(props)}
                addressType="shipping"
                isSameAsShipping={null}
                onChange={onShippingChange}
                order={order}
                onRegionsLoaded={setShippingRegionOptions}
              />
            )}
            <Address
              {...getRootContainerProps(props)}
              addressType="billing"
              isSameAsShipping={isPargoPickup ? null : order.isSameAsShipping ?? true}
              onChange={onBillingChange}
              order={order}
              onRegionsLoaded={identity}
            />
            <AdditionalInfo {...getRootContainerProps(props)} />
            <Event
              {...getRootContainerProps(props)}
              eventDateLocationId={order.eventDateLocationId ?? null}
              eventId={order.eventId ?? null}
              onChange={onEventChange}
            />
            <Variants {...getRootContainerProps(props)} />
            {props.shouldPay && <Payment {...getRootContainerProps(props)} billingShipingInfo={order} />}

            {!props.isSideBar && (
              <OrderSummary
                {...getRootContainerProps(props)}
                isEstimatedTax={isEstimatedTax}
                onApplyCoupon={onApplyCoupon}
                onRemoveCoupon={onRemoveCoupon}
              />
            )}

            <SpecialOffer
              {...getRootContainerProps(props)}
              includeOffer={order.includeOffer}
              onChange={onIncludeOfferChange}
            />

            {props.consentLocation === "above" && (
              <Consent {...getRootContainerProps(props)} onChange={onConsentChange} />
            )}
            <div style={SINGLE_BUTTONS_STYLE}>
              <ModuleButton
                {...getRootContainerProps(props)}
                isDisabled={!props.canPlaceOrder || props.status === "Saving" || isPlacing}
                onClick={onPlaceOrder}
                caption={props.status === "AdjustingOrderInfo" ? i18n.calculatingOrderValues : undefined}
              />
            </div>
            {props.consentLocation === "below" && (
              <Consent {...getRootContainerProps(props)} onChange={onConsentChange} />
            )}

            {!!props.error && <Alert color={SystemColor.Red}>{props.error}</Alert>}
          </div>
          {props.isSideBar && (
            <Sticky className="sidebar" bottomBoundary={`${getEntitySelector(props.id)} .checkout_content`} top={0}>
              <OrderSummary
                {...getRootContainerProps(props)}
                isEstimatedTax={isEstimatedTax}
                onApplyCoupon={onApplyCoupon}
                onRemoveCoupon={onRemoveCoupon}
              />
            </Sticky>
          )}
          <InventoryFailureModal {...getEntityRef(props)} />
        </div>
      </FormStyleProvider>
    </Context.Provider>
  );
};
